import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Textarea } from "../../../../components/ui/textarea";
import { Checkbox } from "../../../../components/ui/checkbox";
import { Cloud, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "../../../../components/ui/alert";
import { type KnowledgeBase } from "@/api/incidentApi";
import AdvanceSettings from "./AdvanceSettings";
import { AgentFormDataType } from "../../type";
import { providers } from "../../utils";
import ProviderSelection from "./ProviderSelection";
import ProviderConfiguration from "./ProviderConfiguration";

interface ExternalRAGAgentFormProps {
  onSubmit?: (data: any) => void;
  onCancel?: () => void;
  knowledgeBases?: KnowledgeBase[];
  categories?: string[];
  isSubmitLoading: boolean;
}

const ExternalRAGAgentForm: React.FC<ExternalRAGAgentFormProps> = ({
  onSubmit,
  onCancel,
  knowledgeBases = [],
  categories = [],
  isSubmitLoading,
}) => {
  const [formData, setFormData] = useState<AgentFormDataType>({
    name: "",
    description: "",
    provider: "",
    model_name: "",
    endpoint_url: "",
    api_key: "",
    region: "us-east-1",
    project_id: "",
    resource_group: "",
    knowledge_base_ids: [],
    categories: [],
    system_prompt:
      "You are an expert IT incident response specialist with access to comprehensive knowledge bases. Analyze incidents thoroughly and provide structured, actionable recommendations based on established procedures and best practices.",
    temperature: 0.7,
    max_tokens: 2000,
    confidence_threshold: 0.7,
    auto_assign: false,
    tags: "",
  });

  const [selectedProvider, setSelectedProvider] = useState<string>("");
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const getCurrentProvider = () => providers.find((p) => p.id === selectedProvider);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!selectedProvider) newErrors.provider = "Provider is required";
    if (!formData.model_name) newErrors.model_name = "Model is required";

    const provider = getCurrentProvider();
    if (provider) {
      // if (provider.fields.includes("api_key") && !formData.api_key.trim()) {
      //   newErrors.api_key = "API key is required";
      // }
      if (provider.fields.includes("endpoint_url") && !formData.endpoint_url.trim()) {
        newErrors.endpoint_url = "Endpoint URL is required";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    const submitData = {
      ...formData,
      provider: selectedProvider,
      tags: formData.tags
        .split(",")
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0),
    };

    try {
      await onSubmit?.(submitData);
    } catch (error: any) {
      setErrors({ submit: error?.message || "An error occurred" });
    }
  };

  const handleProviderChange = (providerId: string) => {
    setSelectedProvider(providerId);
    setFormData((prev) => ({
      ...prev,
      model_name: "",
      endpoint_url: "",
      api_key: "",
      region: providerId === "aws_bedrock" ? "us-east-1" : prev.region,
      project_id: "",
      resource_group: "",
    }));
  };

  const getCostBadgeColor = (cost: string) => {
    switch (cost) {
      case "Budget Friendly":
      case "Cost Effective":
        return "bg-green-100 text-green-800";
      case "Premium":
      case "High Performance":
        return "bg-red-100 text-red-800";
      case "Open Source":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Cloud className="h-5 w-5" />
            Create External RAG Agent
          </CardTitle>
          <p className="text-sm text-gray-600">
            Integrate with external cloud providers to leverage their advanced language models
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Agent Name *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., AWS Bedrock Incident Analyzer"
                  className={errors.name ? "border-red-500" : ""}
                />
                {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Description</label>
                <Input
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, description: e.target.value }))
                  }
                  placeholder="Brief description of the agent's purpose"
                />
              </div>
            </div>

            {/* Provider Selection */}
            <ProviderSelection
              errors={errors}
              selectedProvider={selectedProvider}
              handleProviderChange={handleProviderChange}
              formData={formData}
              setFormData={setFormData}
              getCostBadgeColor={getCostBadgeColor}
            />

            {/* Provider Configuration */}
            {/* <ProviderConfiguration selectedProvider={selectedProvider} formData={formData} setFormData={setFormData} errors={errors} /> */}

            {/* Knowledge Bases and Categories */}
            {!!knowledgeBases?.length && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Knowledge Bases</label>
                  <div className="max-h-32 overflow-y-auto space-y-2 border rounded-md p-2">
                    {knowledgeBases.map((kb) => (
                      <div key={kb.id} className="flex items-center space-x-2">
                        <Checkbox
                          checked={formData.knowledge_base_ids.includes(kb.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setFormData((prev) => ({
                                ...prev,
                                knowledge_base_ids: [...prev.knowledge_base_ids, kb.id],
                              }));
                            } else {
                              setFormData((prev) => ({
                                ...prev,
                                knowledge_base_ids: prev.knowledge_base_ids.filter(
                                  (id) => id !== kb.id
                                ),
                              }));
                            }
                          }}
                        />
                        <span className="text-sm">{kb.name}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Categories</label>
                  <div className="max-h-32 overflow-y-auto space-y-2 border rounded-md p-2">
                    {categories.map((category) => (
                      <div key={category} className="flex items-center space-x-2">
                        <Checkbox
                          checked={formData.categories.includes(category)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setFormData((prev) => ({
                                ...prev,
                                categories: [...prev.categories, category],
                              }));
                            } else {
                              setFormData((prev) => ({
                                ...prev,
                                categories: prev.categories.filter((c) => c !== category),
                              }));
                            }
                          }}
                        />
                        <span className="text-sm">{category}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* System Prompt */}
            <div className="space-y-2">
              <label className="text-sm font-medium">System Prompt</label>
              <Textarea
                value={formData.system_prompt}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, system_prompt: e.target.value }))
                }
                rows={4}
                placeholder="Define the agent's role and behavior..."
              />
            </div>

            {/* Advanced Settings */}
            {/* <AdvanceSettings
              showAdvanced={showAdvanced}
              setShowAdvanced={setShowAdvanced}
              formData={formData}
              setFormData={setFormData}
            /> */}

            {/* Tags */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Tags</label>
              <Input
                value={formData.tags}
                onChange={(e) => setFormData((prev) => ({ ...prev, tags: e.target.value }))}
                placeholder="production, incident-response, automated (comma-separated)"
              />
            </div>

            {/* Error Display */}
            {errors.submit && (
              <Alert className="border-red-200">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">{errors.submit}</AlertDescription>
              </Alert>
            )}

            {/* Actions */}
            <div className="flex gap-4 pt-4">
              <Button type="submit" className="flex-1" disabled={isSubmitLoading}>
                {isSubmitLoading ? "Creating..." : "Create External RAG Agent"}
              </Button>
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ExternalRAGAgentForm;
